import axios from "axios";
import { toast } from "react-toastify";
import { DateRangePicker, DatePicker } from "rsuite";
import { useNavigate } from "react-router-dom";
import { ResizeObserver } from 'resize-observer';
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { logout, reset } from "../../../features/auth/authSlice";
import userTypeConsts from "../../../constants/userTypeConsts.js";
import { reset as invoiceReset } from "../../../features/invoice/invoiceSlice.js";
import { setAutomaticInvoiceUser } from "../../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../../features/appSettings/appSettingsSlice.js";

const InvoiceModal = ({ isAutomaticInv }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useState([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [invTypeDropdownOpen, setInvTypeDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSelectedId, setUserSelectedId] = useState('');
  const [selUserEmail, setSelUserEmail] = useState([]);
  const [selUserCategory, setSelUserCategory] = useState([]);
  const [invType, setInvType] = useState("Regular");
  const [isUserLoading, setIsUserLoading] = useState(true);
  const userDropdownRef = useRef(null);
  const invTypeDropdownRef = useRef(null);
  const dropdownListRef = useRef(null);
  const [errors, setErrors] = useState({ user: "", date: "" });
  const { appSettings } = useSelector((state) => state.appSettings);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 991);
  const [fromCalendarOpen, setFromCalendarOpen] = useState(false);
  const [toCalendarOpen, setToCalendarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDateStr = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) return "DD/MM/YYYY";
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  let token = null;
  const user = useSelector(
    (state) => state.auth.user
  )
  if (user) {
    if (user && user.token) {
      token = user.token;
    }
  }

  const handleSelect = (ranges) => {
    // rsuite DateRangePicker returns [startDate, endDate] or null
    if (ranges && Array.isArray(ranges) && ranges[0] && ranges[1]) {
      // Create new Date objects to ensure state updates
      const startDate = new Date(ranges[0]);
      const endDate = new Date(ranges[1]);
      setDateRange({
        startDate: startDate,
        endDate: endDate,
        key: "selection",
      });
    } else if (ranges === null) {
      // Handle clearing the selection
      setDateRange({
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      });
    }
    setErrors(prev => ({ ...prev, date: "" }));
  };

  // Memoize the date range value to ensure proper updates
  // Compare dates by their time value to detect actual changes
  const dateRangeValue = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      // Return array with Date objects - rsuite expects Date objects
      return [dateRange.startDate, dateRange.endDate];
    }
    return null;
  }, [
    dateRange.startDate?.getTime(),
    dateRange.endDate?.getTime()
  ]);
  const SUPER_ADMIN = 1;
  const ADMIN = 2;
  const ref = useRef(null);
  let resizeObserver = null;


  useEffect(() => {
    // Create a new ResizeObserver instance
    resizeObserver = new ResizeObserver(entries => {
      // Handle resize event
    });

    if (ref.current) {
      // Observe the target element
      resizeObserver.observe(ref.current);
    }

    // Cleanup function
    return () => {
      if (resizeObserver) {
        // Disconnect the observer when component unmounts
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };
  }, []);

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        setIsUserLoading(true);
        const response = await axios.get("/api/user/user-id", {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });
        setUserId(response.data);
      } catch (error) {
        console.log(error);
        global.config.slackMessage(error.toString());
        if (error.response?.data?.status === "deleted") {
          localStorage.removeItem("user");
          await dispatch(logout());
          await dispatch(reset());
          await dispatch(invoiceReset());
          await dispatch(headerReset());
          await dispatch(appSettingsReset());
          toast.error('This user is deleted!', {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          navigate("/");
          return;
        }
        toast.error("Something went wrong", {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      } finally {
        setIsUserLoading(false);
      }
    };

    fetchUserId();
  }, []);

  const handleToggleUserDropdown = () => {
    setUserSearchTerm('');
    setUserDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleUserSearchInputChange = (e) => {
    setUserSearchTerm(e.target.value);
  };

  const filteredUsers = userId.filter((user) => {
    const fullName = user.first_name
      ? `${user.first_name} ${user.middle_name || ''} ${user.last_name}`
      : `${user.company_first_name ? `${user.company_first_name} ${user.company_last_name || ''}` : user.company_name}`;
    return fullName?.toLowerCase().includes(userSearchTerm?.toLowerCase());
  });

  const handleSelectUser = (selectedUserId) => {
    setErrors(prev => ({ ...prev, user: "" }));
    setUserSelectedId(selectedUserId);
    const userEmail = userId.find(
      (item) => item._id === selectedUserId
    )?.email;
    setSelUserEmail(userEmail);
    const userCategory = userId.find(
      (item) => item._id === selectedUserId
    )?.user_category;
    setSelUserCategory(userCategory);
    setUserSearchTerm('');
    setUserDropdownOpen(false);
  };

  const handleClickOutsideDropdown = (event) => {
    const isClickInsideUserDropdown = userDropdownRef.current && userDropdownRef.current.contains(event.target);
    const isClickInsideInvTypeDropdown = invTypeDropdownRef.current && invTypeDropdownRef.current.contains(event.target);
    if (!isClickInsideUserDropdown) {
      setUserDropdownOpen(false);
    }
    if (!isClickInsideInvTypeDropdown) {
      setInvTypeDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('click', handleClickOutsideDropdown);
    };
  }, []);

  useEffect(() => {
    // Get the selected item
    const selectedItem = document.querySelector('.selected-item');
    // Get the container element
    const dropdownListContainer = dropdownListRef.current;

    // Scroll to the selected item and center it in the view
    if (selectedItem && dropdownListContainer) {
      selectedItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [userDropdownOpen, userSearchTerm, filteredUsers, userSelectedId]);

  const automaticInvoiceSubmit = async () => {
    var autoData = [];
    const newErrors = { user: "", date: "" };
    let hasError = false;

    if (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) {
      if (!userSelectedId || userSelectedId === "") {
        newErrors.user = "Please select a user";
        hasError = true;
      }
    }
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      newErrors.date = "Please select a date range";
      hasError = true;
    } else if (dateRange.startDate.getTime() === dateRange.endDate.getTime()) {
      newErrors.date = "Please select a different end date";
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) {
      if (newErrors.date) toast.error(newErrors.date, { position: "bottom-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, theme: "light" });
      return;
    }
    {
      // Convert Date objects to ISO strings for Redux serialization
      autoData.push([dateRange.startDate.toISOString(), dateRange.endDate.toISOString()]);
      if (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) {

        autoData.push(userSelectedId);
      } else {
        autoData.push(user._id);
      }

      autoData.push(invType);
      await dispatch(setAutomaticInvoiceUser(autoData));
      navigate("/user/invoice/auto/create");
    }
  };

  const manualInvoiceSubmit = async () => {
    var autoData = [];
    const newErrors = { user: "", date: "" };
    let hasError = false;

    if (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) {
      if (!userSelectedId || userSelectedId === "") {
        newErrors.user = "Please select a user";
        hasError = true;
      }
    }
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      newErrors.date = "Please select a date range";
      hasError = true;
    } else if (dateRange.startDate.getTime() === dateRange.endDate.getTime()) {
      newErrors.date = "Please select a different end date";
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) {
      if (newErrors.date) toast.error(newErrors.date, { position: "bottom-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, theme: "light" });
      return;
    }
    {
      // Convert Date objects to ISO strings for Redux serialization
      autoData.push([dateRange.startDate.toISOString(), dateRange.endDate.toISOString()]);
      if (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) {
        autoData.push(userSelectedId);
        autoData.push(selUserEmail);
        autoData.push(selUserCategory);
      } else {
        autoData.push(user._id);
        autoData.push(user.email);
        autoData.push(user.userCategory);
      }
      autoData.push(invType);
      await dispatch(setAutomaticInvoiceUser(autoData));
      navigate("/user/invoice/manual/create");
    }
  };

  return (
    <div className="invoice-modal" ref={ref}>
      <h2 className="invoice-modal-main-head">Type of Invoice</h2>
      {(user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && (
        <div className="form-group-1modal userseloutter-1">
          <label htmlFor="user-select">User *</label>
          {isUserLoading ? (
            <div
              className="custom-dropdown-user-new-outer"
              style={{
                pointerEvents: "none",
                cursor: "default",
                position: "relative",
                width: "100%",
              }}
            >
              <div
                className="custom-dropdown-user-new"
                style={{
                  backgroundColor: "#e5e7eb",
                  border: "0.2px solid rgba(106, 121, 136, 0.7)",
                  borderRadius: "7px",
                  width: "100%",
                  height: "43px",
                  paddingLeft: "16px",
                  paddingRight: "40px",
                  display: "flex",
                  alignItems: "center",
                  animation: "pulse 1.5s ease-in-out infinite",
                  boxSizing: "border-box",
                  position: "relative",
                  backgroundImage: "none",
                }}
              >
                <span
                  style={{
                    width: "120px",
                    height: "19px",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                    display: "inline-block",
                  }}
                />
              </div>
            </div>
          ) : (
            // <select className={errorMessage && errorMessage.field === 'both' ? "select-modal-mainM error" : "select-modal-mainM"}
            //   id="user-select"
            //   value={userSelectedId}
            //   onChange={(e) => {
            //     setErrors(prev => ({ ...prev, user: "", date: "" }));
            //     setUserSelectedId(e.target.value);
            //     const userEmail = userId.find(
            //       (item) => item._id === e.target.value
            //     )?.email;
            //     setSelUserEmail(userEmail);
            //     const userCategory = userId.find(
            //       (item) => item._id === e.target.value
            //     )?.user_category;
            //     setSelUserCategory(userCategory);
            //   }}
            //   required
            // >
            //   <option value="">Select User</option>
            //   {userId.length > 0
            //     ? userId
            //       .slice() // Create a copy of the array to avoid mutating the original array
            //       .sort((a, b) => {
            //         // Sort the array alphabetically based on user names
            //         const nameA = a.first_name
            //           ? `${a.first_name} ${a.middle_name || ""} ${a.last_name}`
            //           : a.company_name;
            //         const nameB = b.first_name
            //           ? `${b.first_name} ${b.middle_name || ""} ${b.last_name}`
            //           : b.company_name;
            //         return nameA.localeCompare(nameB);
            //       })
            //       .map((item, index) => (
            //         <option value={item._id} key={index}>
            //           {item.first_name ? (
            //             <>
            //               {item.first_name}{" "}
            //               {item.middle_name && item.middle_name !== "null"
            //                 ? item.middle_name + " "
            //                 : ""}
            //               {item.last_name}{" "}
            //             </>
            //           ) : (
            //             <>
            //               {item.company_name} {" (Company) "}
            //             </>
            //           )}
            //         </option>
            //       ))
            //     : ""}
            // </select>
            <div ref={userDropdownRef} className={errors.user ? "select-modal-mainM error custom-dropdown-user-new-outer inv-field-error" : "select-modal-mainM custom-dropdown-user-new-outer"}>
              <span
                id="user-select"
                onClick={handleToggleUserDropdown}
                className="custom-dropdown-user-new"
              >
                <span>
                  {userSelectedId ? (
                    userId.find((user) => user._id === userSelectedId)?.first_name ? (
                      <>
                        {userId.find((user) => user._id === userSelectedId).first_name}{' '}
                        {userId.find((user) => user._id === userSelectedId).middle_name &&
                          userId.find((user) => user._id === userSelectedId).middle_name !== 'null'
                          ? userId.find((user) => user._id === userSelectedId).middle_name + ' '
                          : ''}
                        {userId.find((user) => user._id === userSelectedId).last_name}{' '}
                      </>
                    ) : (
                      <>
                        {userId.find((user) => user._id === userSelectedId)?.company_first_name
                          ? `${userId.find((user) => user._id === userSelectedId).company_first_name} ${userId.find((user) => user._id === userSelectedId).company_last_name
                            ? userId.find((user) => user._id === userSelectedId).company_last_name
                            : ''
                          }`
                          : `${userId.find((user) => user._id === userSelectedId)?.company_name || ''}(Company)`}
                      </>
                    )
                  ) : (
                    'Select User'
                  )}
                </span>
                <svg width="18" height="9" viewBox="0 0 18 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-chevron-icon" aria-hidden="true">
                  <path d="M1.5 1.5L9 7.5L16.5 1.5" stroke="#405BFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {(user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && userDropdownOpen && (
                <div className="custom-dropdown-new" ref={dropdownListRef}>
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search..."
                    value={userSearchTerm}
                    onChange={handleUserSearchInputChange}
                  />
                  <ul className="client-list">
                    {filteredUsers.length > 0 &&
                      filteredUsers
                        .slice()
                        .sort((a, b) => {
                          const nameA = a.first_name
                            ? `${a.first_name} ${a.middle_name || ''} ${a.last_name}`
                            : `${a.company_first_name ? a.company_first_name + (a.company_last_name && a.company_last_name) : a.company_name}`;
                          const nameB = b.first_name
                            ? `${b.first_name} ${b.middle_name || ''} ${b.last_name}`
                            : `${b.company_first_name ? b.company_first_name + (b.company_last_name && b.company_last_name) : b.company_name}`;
                          return nameA.localeCompare(nameB);
                        })
                        .map((item) => (
                          <li key={item._id} onClick={() => handleSelectUser(item._id)} className={userSelectedId === item._id ? 'selected-item' : ''}>
                            {item.first_name ? (
                              <>
                                {item.first_name}{' '}
                                {item.middle_name && item.middle_name !== 'null' ? item.middle_name + ' ' : ''}
                                {item.last_name}{' '}
                              </>
                            ) : (
                              <>
                                {item.company_first_name
                                  ? `${item.company_first_name} ${item.company_last_name ? item.company_last_name : ''}`
                                  : `${item.company_name || ''}(Company)`}
                              </>
                            )}
                          </li>
                        ))
                    }
                  </ul>
                </div>
              )
              }
            </div>
          )}

        </div>
      )}
      {isAutomaticInv === false && (
        <div className="form-group-1modal userseloutter-1">
          <label htmlFor="user-select">Invoice Type *</label>
          <div ref={invTypeDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
            <span className="custom-dropdown-user-new" onClick={() => setInvTypeDropdownOpen(!invTypeDropdownOpen)}>
              <span>{invType ? (invType === 'Regular' ? 'Create Fees Invoice' : 'Create Reimbursement Invoice') : 'Select Invoice Type'}</span>
              <svg width="18" height="9" viewBox="0 0 18 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-chevron-icon" aria-hidden="true">
                <path d="M1.5 1.5L9 7.5L16.5 1.5" stroke="#405BFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            {invTypeDropdownOpen && (
              <div className="custom-dropdown-new">
                <ul className="invoice-type-list">
                  <li className={invType === 'Regular' ? 'selected-item' : ''} onClick={() => { setInvType('Regular'); setInvTypeDropdownOpen(false); }}>Create Fees Invoice</li>
                  <li className={invType === 'Reimbursement' ? 'selected-item' : ''} onClick={() => { setInvType('Reimbursement'); setInvTypeDropdownOpen(false); }}>Create Reimbursement Invoice</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="form-group-1modal range-picinmodal-1">
        <label htmlFor="date-range">Date Range *</label>
        {isMobile ? (
          <div className="mobile-date-range-container">
            <div className="mobile-date-field" onClick={() => setFromCalendarOpen(true)}>
              <span className="mobile-date-label">From</span>
              <span className="mobile-date-val">{formatDateStr(dateRange.startDate)}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div className="mobile-date-field" onClick={() => setToCalendarOpen(true)}>
              <span className="mobile-date-label">To</span>
              <span className="mobile-date-val">{formatDateStr(dateRange.endDate)}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div style={{ opacity: 0, height: 0, overflow: 'hidden', position: 'absolute', pointerEvents: 'none' }}>
              <DatePicker
                open={fromCalendarOpen}
                onClose={() => setFromCalendarOpen(false)}
                oneTap
                format="dd/MM/yyyy"
                value={dateRange.startDate}
                onChange={(val) => {
                  if (val) {
                    setDateRange((prev) => ({ ...prev, startDate: new Date(val) }));
                    setErrors((prev) => ({ ...prev, date: "" }));
                  }
                  setFromCalendarOpen(false);
                }}
                placement="auto"
                isoWeek={appSettings.weekStartDay === 'Monday'}
              />
              <DatePicker
                open={toCalendarOpen}
                onClose={() => setToCalendarOpen(false)}
                oneTap
                format="dd/MM/yyyy"
                value={dateRange.endDate}
                onChange={(val) => {
                  if (val) {
                    setDateRange((prev) => ({ ...prev, endDate: new Date(val) }));
                    setErrors((prev) => ({ ...prev, date: "" }));
                  }
                  setToCalendarOpen(false);
                }}
                placement="auto"
                isoWeek={appSettings.weekStartDay === 'Monday'}
              />
            </div>
          </div>
        ) : (
          <div className={errors.date ? "date-picker-outer date-picker-outer-all inv-field-error" : "date-picker-outer date-picker-outer-all"}>
            <div className="custom-picker-icon custom-picker-icon-all">
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 14 14"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
                className="rs-picker-toggle-caret rs-icon"
                aria-label="calendar"
                data-category="legacy"
              >
                <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z"></path>
              </svg>
            </div>
            <div className="custoom-icon-calender custoom-icon-calender-all">
              <DateRangePicker
                showOneCalendar
                className={errors.date ? "errorDate" : ""}
                id="date-range"
                format="dd/MM/yyyy"
                placeholder="Select a date range"
                value={dateRangeValue}
                onChange={handleSelect}
                placement="auto"
                isoWeek={appSettings.weekStartDay === 'Monday'}
              />
            </div>
          </div>
        )}
      </div>
      <div className="form-group-1modal submittseloutter-1">
        {isAutomaticInv === true ? (
          <button
            className="generate-button modalsubmitbtn-1"
            onClick={automaticInvoiceSubmit}
          >
            Generate Invoice
          </button>
        ) : (
          <button className="create-button modalsubmitbtn-1" onClick={manualInvoiceSubmit}>
            Create Invoice
          </button>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceModal;